import { backupRecords, chunks } from './baseStorage';
import { hasEnoughSpace } from './storageQuota';
import logger from './logger';
import {
  getExtensionBackupRecords,
  isExtensionInstalled,
} from './extensionRecovery';

// Add constants at the top of the file
const MILLISECONDS_IN_DAY = 1000 * 60 * 60 * 24;
const DAYS_FOR_CLEANUP = 10;
const DEFAULT_BATCH_SIZE = 10;

export type BackupRecordStatus =
  | 'pending'
  | 'dismissed'
  | 'uploaded'
  | 'recovered';

export interface BackupRecord {
  teamId?: string;
  folderId?: string;
  fileId: string;
  uploadId?: string;
  startedAt: number;
  status?: BackupRecordStatus; // Optional for backwards compatibility, defaults to 'pending'
  contentType?: string; // Optional, defaults to 'video/x-matroska'
  title?: string; // Optional title for the recording
}

export const addBackupRecord = async (fileId: string, value: BackupRecord) => {
  // Estimate size of backup record (small, typically < 1KB)
  const estimatedSize = 1024; // 1KB for metadata
  const hasSpace = await hasEnoughSpace(estimatedSize);

  if (!hasSpace) {
    logger.warn(
      'Insufficient storage to add backup record - skipping backup metadata',
    );
    // Don't throw - allow recording to continue without backup
    return;
  }

  await backupRecords.setItem(fileId, value);
};

export const updateBackupRecordStatus = async (
  fileId: string,
  status: BackupRecordStatus,
) => {
  const record = (await backupRecords.getItem(fileId)) as BackupRecord;
  if (record) {
    record.status = status;
    await backupRecords.setItem(fileId, record);
  }
};

export const getBackupRecords = async (teamId?: string) => {
  // Get backup records from browser storage
  const browserRecords = await (Promise.all(
    (await backupRecords.keys()).map((key) => backupRecords.getItem(key)),
  ) as Promise<BackupRecord[]>);

  // Get backup records from extension (if installed)
  let extensionRecords: BackupRecord[] = [];
  if (isExtensionInstalled()) {
    try {
      const extensionRecordsMap = await getExtensionBackupRecords();
      extensionRecords = Object.values(extensionRecordsMap);
    } catch (error) {
      logger.warn('Failed to get extension backup records:', error);
    }
  }

  // Merge both sources, removing duplicates (extension records take priority)
  const allRecordsMap = new Map<string, BackupRecord>();

  // Add browser records first
  browserRecords.forEach((record) => {
    allRecordsMap.set(record.fileId, record);
  });

  // Add/override with extension records
  extensionRecords.forEach((record) => {
    allRecordsMap.set(record.fileId, record);
  });

  const allRecords = Array.from(allRecordsMap.values());

  if (teamId) {
    return allRecords.filter((r) => r.teamId === teamId);
  }

  return allRecords;
};

export const deleteBackupRecord = async (fileId: string) => {
  await backupRecords.removeItem(fileId);
};

const makeChunkKey = (fileId: string, partNumber: number) => {
  return `${fileId}_${partNumber}`;
};

export const saveChunk = async (
  fileId: string,
  partNumber: number,
  value: any,
) => {
  // Check storage quota before saving chunk
  const chunkSize = value?.size || 0;
  const hasSpace = await hasEnoughSpace(chunkSize);

  if (!hasSpace) {
    logger.warn(
      `Insufficient storage to save chunk ${partNumber} (${chunkSize} bytes) - skipping backup`,
    );
    // Don't throw - allow recording to continue without backup
    return;
  }

  await chunks.setItem(makeChunkKey(fileId, partNumber), value);
};

export const loadChunk = async (fileId: string, partNumber: number) => {
  return chunks.getItem(makeChunkKey(fileId, partNumber)) as Promise<Blob>;
};

export const listChunks = async (fileId?: string) => {
  const keys = await chunks.keys();

  if (fileId) {
    return keys.filter((key) => key.startsWith(fileId));
  }

  return keys;
};

export const deleteChunks = async (fileId: string) => {
  const keys = await listChunks(fileId);
  await Promise.all(
    keys.map(async (key) => {
      await chunks.removeItem(key);
    }),
  );
};

/**
 * Cleanup old backups without specifying the team. Delete backups older than specified days.
 * This is the ONLY place where backup records and chunks should be deleted
 * unless user deletes them manually, or the recovery process is completed.
 * @param batchSize - Number of records to delete in each batch (default 5)
 * @param daysOld - Number of days after which backups are considered old (default 10)
 * @returns Number of records deleted
 */
export const cleanupOldBackups = async (
  batchSize = DEFAULT_BATCH_SIZE,
  daysOld = DAYS_FOR_CLEANUP,
) => {
  try {
    const allRecords = await backupRecords
      .keys()
      .then((keys) =>
        Promise.all(
          keys.map(
            (key) => backupRecords.getItem(key) as Promise<BackupRecord>,
          ),
        ),
      );

    const cutoffDate = Date.now() - MILLISECONDS_IN_DAY * daysOld;
    const oldRecords = allRecords
      .filter((record) => record.startedAt < cutoffDate)
      .sort((a, b) => a.startedAt - b.startedAt); // Sort oldest first

    // Process only a batch of oldest records
    const toDelete = oldRecords.slice(0, batchSize);

    await Promise.all(
      toDelete.map(async (record) => {
        console.log('Deleting backup record', record);
        await deleteBackupRecord(record.fileId);
        await deleteChunks(record.fileId);
      }),
    );

    return toDelete.length; // Return number of deleted records
  } catch (error) {
    console.error('Backup cleanup failed:', error);
    return 0;
  }
};
